/**
 * Subtle haptics, in the brief's hierarchy and the playground's patterns
 * (docs/mockups/sound-feel.html): a letter is barely there, a clue a light tap, an answer
 * a double tap, Flow a short motif, a wrong check one firm knock, completion the strongest.
 * Each fires beside its sound (game/sound.ts); nothing is ever communicated by haptics alone.
 *
 * - Android/Chromium: `navigator.vibrate`, only on a touch device (desktop Chrome has the
 *   function but nothing to vibrate).
 * - iPhone/iPad (no `vibrate`): Safari gives a light system tick when a switch control is
 *   flipped, so a hidden `<input type="checkbox" switch>` in a label is clicked once per
 *   "on" beat of the pattern. Ticks after the first may not fire on every iOS version.
 * - Anywhere else, or with Haptics off in Settings: a silent no-op.
 */
import { settings } from '../app/settings.ts';

export const HAPTICS = {
	key: 6,
	del: 4,
	clue: 8,
	solved: [12, 40, 20],
	flow: [10, 40, 18],
	wrong: 30,
	completed: [18, 50, 18, 50, 18, 90, 40, 60, 110],
} as const satisfies Record<string, number | readonly number[]>;

export type HapticKind = keyof typeof HAPTICS;

/** How this device can buzz: `vibrate`, the iOS switch tick, or not at all. */
export type HapticMethod = 'vibrate' | 'ios' | 'none';

export function hapticMethod(nav: Navigator | undefined = typeof navigator === 'undefined' ? undefined : navigator): HapticMethod {
	if (!nav) return 'none';
	const touch = (nav.maxTouchPoints ?? 0) > 0;
	if (typeof nav.vibrate === 'function') return touch ? 'vibrate' : 'none';
	const ios = /iP(hone|ad|od)/.test(nav.userAgent ?? '') || (nav.platform === 'MacIntel' && (nav.maxTouchPoints ?? 0) > 1);
	return ios ? 'ios' : 'none';
}

let method: HapticMethod | null = null;
let iosLabel: HTMLLabelElement | null = null;

/** The hidden switch (made once), whose flip gives Safari's tick. Clicks on it go nowhere else. */
function iosSwitch(): HTMLLabelElement | null {
	if (iosLabel?.isConnected) return iosLabel;
	if (typeof document === 'undefined' || !document.body) return null;
	const label = document.createElement('label');
	label.setAttribute('aria-hidden', 'true');
	label.style.cssText = 'position:fixed;left:-100px;top:0;width:1px;height:1px;overflow:hidden;opacity:.01;pointer-events:none';
	const input = document.createElement('input');
	input.type = 'checkbox';
	input.setAttribute('switch', '');
	input.tabIndex = -1;
	label.append(input);
	label.addEventListener('click', (e) => e.stopPropagation());
	document.body.append(label);
	iosLabel = label;
	return label;
}

function tick(): void {
	const label = iosSwitch();
	if (!label) return;
	const before = document.activeElement;
	label.click();
	// Keep focus where it was (the board's typing target, a dialog's control).
	if (document.activeElement !== before && before instanceof HTMLElement) before.focus({ preventScroll: true });
}

/** The vibration pattern for an event, as an array (on, off, on, … in milliseconds). */
export const hapticPattern = (kind: HapticKind): number[] => {
	const p = HAPTICS[kind];
	return typeof p === 'number' ? [p] : [...p];
};

export function haptic(kind: HapticKind): void {
	try {
		if (!settings().get().haptics) return;
		method ??= hapticMethod();
		if (method === 'none') return;
		const pattern = hapticPattern(kind);
		if (method === 'vibrate') {
			navigator.vibrate(pattern.length === 1 ? pattern[0]! : pattern);
			return;
		}
		let t = 0;
		pattern.forEach((ms, i) => {
			if (i % 2 === 0) {
				if (t === 0) tick();
				else window.setTimeout(tick, t);
			}
			t += ms;
		});
	} catch {
		// Some browsers throw when vibration is blocked (e.g. before a user gesture).
	}
}

/** Tests: detect the device again. */
export function resetHaptics(): void {
	method = null;
	iosLabel?.remove();
	iosLabel = null;
}

export function prefersReducedMotion(): boolean {
	try {
		return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
	} catch {
		return false;
	}
}
